import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Grid } from '@material-ui/core';

import AssurityIcon from 'common/assets/assurity-offer-icon.svg';
import { Card } from 'core/components/card';
import Label from 'core/components/label/label';
import { Tooltip } from 'core/components/tooltip';
import Button from 'core/components/button/button';
import useSession from 'core/hooks/use-session';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: 32,
    marginBottom: theme.spacing(4)
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  learnMoreLink: {
    width: 'auto',
    height: 'auto',
    borderRadius: theme.spacing(2),
    color: theme.colorPalette.orange_50,
    padding: 0,
    marginLeft: theme.spacing(0.2),
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  learnMoreLinkText: {
    fontSize: 10,
    fontWeight: 400,
    color: theme.colorPalette.orange_50
  },
  emptyCard: {
    marginTop: 16,
    borderRadius: theme.sizes.roundCorners,
    fontSize: 16,
    fontWeight: 400,
    padding: '16px 8px',
    textAlign: 'center',
    backgroundColor: theme.colorPalette.beige_50
  },
  title: {
    color: theme.colorPalette.green_80,
    fontSize: 20,
    fontWeight: 400
  },
  innerCard: {
    marginTop: 16,
    borderRadius: theme.sizes.innerCorners,
    overflow: 'hidden'
  },
  innerCardTop: {
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colorPalette.beige_50
  },
  innerCardBottom: {
    display: 'flex',
    padding: 16,
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colorPalette.beige_25
  },
  innerCardTopTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.colorPalette.green_90
  },
  innerCardTopSubtitle: {
    fontSize: 16,
    fontWeight: 400,
    color: theme.colorPalette.green_80
  },
  icon: {
    height: 40,
    width: 40,
    marginLeft: 8
  },
  innerCardBottomTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colorPalette.green_90,
    display: 'block'
  },
  innerCardBottomValue: {
    fontSize: 24,
    color: theme.colorPalette.green_90,
    display: 'flex',
    alignItems: 'center'
  },
  pricePerMonthText: {
    fontSize: 12,
    color: theme.colorPalette.green_80,
    marginLeft: 4
  },
  divider: {
    width: 1,
    height: 52,
    backgroundColor: theme.colorPalette.green_90,
    opacity: '0.5'
  },
  additionalOffersSection: {
    marginTop: 16
  },
  additionalOffersTitle: {
    display: 'flex',
    alignItems: 'center'
  },
  additionalOffersTitleText: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.colorPalette.green_75
  },
  additionalOffersBodyText: {
    fontSize: 16,
    color: theme.colorPalette.green_80
  },
  actionSection: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'flex-end'
  }
}));

const personalizedLifeOfferTooltipText =
  "We've tailored this life insurance offer using verified data from reports on this offer/policy. The suggested coverage amount is chosen based on remaining mortgage or overall debt, assuming no tobacco use. This amount is adjustable, and the final price may vary based on details in the final offer.";
const additionalLifeOffersTooltipText =
  "We've identified eligible offers for these individuals on the offer/policy. If someone isn't listed, we unfortunately could not get an offer for them.";

const internalAgentTooltipHREF = '6c39eadc-8285-4468-b4a2-bb59ae35cd42';
const externalAgentTooltipHREF = '825b4156-2809-428b-b67c-30be9ad5325f';

export const AssurityOffer = observer(
  ({ assurityTermLifeData, allPeople, onOpenOffersClicked, openOffersButtonDisabled = false }) => {
    const classes = useStyles();
    const { isAgency } = useSession();

    const branchPrimary = allPeople.find((p) => p.isPrimary);
    const branchPrimaryHasLifeOffer = assurityTermLifeData?.eligibleLifeOffers?.find(
      (o) => o.personId === branchPrimary?.id
    );

    let displayedLifeOffer;
    let personToDisplayLifeOfferFor;

    if (branchPrimaryHasLifeOffer) {
      displayedLifeOffer = assurityTermLifeData?.eligibleLifeOffers?.find((o) => o.personId === branchPrimary?.id);
      personToDisplayLifeOfferFor = branchPrimary;
    } else {
      displayedLifeOffer = assurityTermLifeData?.eligibleLifeOffers?.[0];
      personToDisplayLifeOfferFor = allPeople.find((p) => p.id === displayedLifeOffer?.personId);
    }

    // FIXME: 16-01-2025 - The personId returned as eligible for an assurity offer
    // might not be present in the allPeople array for some reason. Hotfix to prevent
    // staff-fe from crashing till we figure this out.
    if (!personToDisplayLifeOfferFor) {
      return null;
    }

    let additionalOffersBodyText;

    const additionalLifeOffersAvailable = assurityTermLifeData?.eligibleLifeOffers?.length > 1;
    if (additionalLifeOffersAvailable) {
      const allLifeOffersExceptDisplayed = assurityTermLifeData?.eligibleLifeOffers?.filter(
        (o) => o.personId !== personToDisplayLifeOfferFor.id
      );
      additionalOffersBodyText = allLifeOffersExceptDisplayed
        .map((o) => allPeople.find((p) => p.id === o.personId))
        .map((p) => `${p.firstName} ${p.lastName}`)
        .join(', ');
    } else {
      additionalOffersBodyText = 'There are no other life offers available';
    }

    const noOffersToDisplay =
      !assurityTermLifeData?.eligibleLifeOffers || assurityTermLifeData.eligibleLifeOffers.length === 0;

    return (
      <Card id="offer_additional_offers" className={classes.card}>
        <div className={classes.cardHeader}>
          <Label className={classes.title}>5 Year Instant Term Life</Label>

          <IconButton
            className={classes.learnMoreLink}
            onClick={() => {
              if (!isAgency) {
                window?.Appcues?.show(internalAgentTooltipHREF);
              } else {
                window?.Appcues?.show(externalAgentTooltipHREF);
              }
            }}
          >
            <div className={classes.learnMoreLinkText}>Learn More</div>
            <img src={arrowIcon} height={14} width={14} alt="Learn More" />
          </IconButton>
        </div>
        {noOffersToDisplay ? (
          <div className={classes.emptyCard}>There are no term life offers for anyone on the offer or policy.</div>
        ) : (
          <>
            <div className={classes.innerCard}>
              <div className={classes.innerCardTop}>
                <div>
                  <Grid container alignItems="center" justifyContent="center">
                    <Label className={classes.innerCardTopTitle}>Personalized Life Offer</Label>
                    <Tooltip text={personalizedLifeOfferTooltipText} labelNextToText />
                  </Grid>
                  <Label className={classes.innerCardTopSubtitle}>
                    {personToDisplayLifeOfferFor.firstName} {personToDisplayLifeOfferFor.lastName}
                  </Label>
                </div>
                <div>
                  <img src={AssurityIcon} className={classes.icon} alt="" />
                </div>
              </div>
              <div className={classes.innerCardBottom}>
                <div>
                  <Label className={classes.innerCardBottomTitle}>Price</Label>
                  <Label className={classes.innerCardBottomValue}>
                    $
                    {displayedLifeOffer?.monthlyPremiumAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    })}
                    <span className={classes.pricePerMonthText}>/ MO</span>
                  </Label>
                </div>
                <div className={classes.divider} />
                <div>
                  <Label className={classes.innerCardBottomTitle}>Coverage</Label>
                  <Label className={classes.innerCardBottomValue}>
                    ${displayedLifeOffer.benefitAmount.toLocaleString()}
                  </Label>
                </div>
              </div>
            </div>
            <div className={classes.additionalOffersSection}>
              <div className={classes.additionalOffersTitle}>
                <Label className={classes.additionalOffersTitleText}>ADDITIONAL LIFE OFFERS</Label>
                <Tooltip text={additionalLifeOffersTooltipText} labelNextToText />
              </div>
              <div className={classes.additionalOffersBodyText}>{additionalOffersBodyText}</div>
            </div>
            <div className={classes.actionSection}>
              <Button
                mode="big"
                variant="contained"
                color="secondary"
                onClick={onOpenOffersClicked}
                disabled={openOffersButtonDisabled}
              >
                See offers
              </Button>
            </div>
          </>
        )}
      </Card>
    );
  }
);

AssurityOffer.propTypes = {
  assurityTermLifeData: PropTypes.object.isRequired,
  allPeople: PropTypes.array,
  onOpenOffersClicked: PropTypes.func,
  openOffersButtonDisabled: PropTypes.bool
};
